import {
  Box,
  Heading,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Code,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  VStack,
  useClipboard,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
  HStack,
  SimpleGrid,
  TableContainer,
  Icon,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  Tag,
  TagLabel,
  TagCloseButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverCloseButton,
  Select,
  FormControl,
  FormLabel,
  Portal,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEllipsisV,
  faEdit,
  faTrash,
  faCode,
  faCopy,
  faCheck,
  faGlobe,
  faEnvelope,
  faLayerGroup,
  faBullhorn,
  faFilter,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import { sitesListAtom } from '../state/sitesAtom';
import { useZones, useZoneMutations } from '../hooks/useZones';
import ZoneModal from '../components/zones/ZoneModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import DateRangePicker from '../components/common/DateRangePicker';
import { dateRangeAtom } from '../state/dateRangeAtom';
import { parseDateRange } from '../hooks/useAnalytics';
import { useToast } from '@chakra-ui/react';
import { useLicenseProtection } from '../hooks/useLicenseProtection';
import { formatFullNumber } from '../utils/format';

function ZoneCard({ zone, onOpenCode, onEdit, onDelete, onCampaignsClick, onSiteClick, onZoneClick, isReadOnly }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      _hover={{ transform: 'translateY(-1px)' }}
      transition="all 0.2s"
    >
      <CardBody>
        <Flex justify="space-between" align="start" mb={3}>
          <Box flex={1}>
            <HStack spacing={2} mb={1}>
              <Box
                w="10px"
                h="10px"
                borderRadius="full"
                bg={zone.status === 'active' ? 'green.400' : 'yellow.400'}
              />
              <Text
                fontWeight="600"
                noOfLines={1}
                color="brand.500"
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onZoneClick && onZoneClick(zone);
                }}
              >
                {zone.name}
              </Text>
            </HStack>
            <Text
              fontSize="sm"
              color="purple.500"
              cursor="pointer"
              _hover={{ textDecoration: 'underline' }}
              onClick={(e) => {
                e.stopPropagation();
                onSiteClick && onSiteClick(zone);
              }}
            >
              {zone.site}
            </Text>
          </Box>
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FontAwesomeIcon icon={faEllipsisV} />}
              variant="ghost"
              size="sm"
              borderRadius="lg"
            />
            <Portal>
              <MenuList zIndex={9999} py={2} borderRadius="xl">
                <MenuItem icon={<FontAwesomeIcon icon={faBullhorn} />} onClick={() => onCampaignsClick && onCampaignsClick(zone)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                  View Campaigns
                </MenuItem>
                <MenuItem icon={<FontAwesomeIcon icon={faCode} />} onClick={() => onOpenCode(zone)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                  Get Code
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  onClick={() => onEdit(zone)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Edit
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  color={isReadOnly ? 'gray.400' : 'red.500'}
                  onClick={() => onDelete(zone)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>

        <HStack spacing={2} mb={4}>
          <Badge variant="outline" borderRadius="full">{zone.size}</Badge>
          <Badge colorScheme={zone.status === 'active' ? 'green' : 'yellow'} borderRadius="full">
            {zone.status}
          </Badge>
          <Badge
            colorScheme={zone.zoneType === 'email' ? 'purple' : 'blue'}
            borderRadius="full"
            display="flex"
            alignItems="center"
            gap={1}
          >
            <FontAwesomeIcon icon={zone.zoneType === 'email' ? faEnvelope : faGlobe} size="xs" />
            {zone.zoneType === 'email' ? 'Email' : 'Web'}
          </Badge>
        </HStack>

        <SimpleGrid columns={2} spacing={3}>
          <Box p={3} bg={statBg} borderRadius="lg" textAlign="center">
            <Text fontSize="xs" color={textSecondary} mb={1}>Impressions</Text>
            <Text fontWeight="700" fontSize="lg" bgGradient="linear(to-r, brand.500, accent.500)" bgClip="text">
              {formatFullNumber(zone.impressions)}
            </Text>
          </Box>
          <Box p={3} bg={statBg} borderRadius="lg" textAlign="center">
            <Text fontSize="xs" color={textSecondary} mb={1}>Clicks</Text>
            <Text fontWeight="700" fontSize="lg" color="brand.500">{formatFullNumber(zone.clicks)}</Text>
          </Box>
          <Box p={3} bg={statBg} borderRadius="lg" textAlign="center">
            <Text fontSize="xs" color={textSecondary} mb={1}>CTR</Text>
            <Text fontWeight="700" fontSize="lg" color="green.500">{zone.ctr}%</Text>
          </Box>
          <Box
            p={3}
            bg={statBg}
            borderRadius="lg"
            textAlign="center"
            cursor={zone.campaigns > 0 ? 'pointer' : 'default'}
            _hover={zone.campaigns > 0 ? { bg: 'purple.50', _dark: { bg: 'purple.900' } } : {}}
            onClick={() => zone.campaigns > 0 && onCampaignsClick && onCampaignsClick(zone)}
            transition="all 0.2s"
          >
            <Text fontSize="xs" color={textSecondary} mb={1}>Campaigns</Text>
            <Text fontWeight="700" fontSize="lg" color="purple.500">{zone.campaigns}</Text>
          </Box>
        </SimpleGrid>

        <Button
          mt={4}
          w="100%"
          size="sm"
          leftIcon={<FontAwesomeIcon icon={faCode} />}
          variant="outline"
          borderRadius="lg"
          onClick={() => onOpenCode(zone)}
        >
          Get Embed Code
        </Button>
      </CardBody>
    </Card>
  );
}

function ZonesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const siteFilter = searchParams.get('site');
  const { isReadOnly, protectAction } = useLicenseProtection();

  // Modal states
  const { isOpen: isCodeOpen, onOpen: onCodeOpen, onClose: onCodeClose } = useDisclosure();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneToDelete, setZoneToDelete] = useState(null);

  // Local filter state
  const [filters, setFilters] = useState({
    site: '',
    status: '',
    zoneType: '',
  });

  // Date range state (shared globally across all tabs)
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);

  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
  }, [setDateRange]);

  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const inputBg = useColorModeValue('white', 'gray.700');

  const toast = useToast();

  // Build API filters from URL params and date range
  const apiFilters = useMemo(() => {
    const dates = parseDateRange(dateRange);
    const params = { start: dates.start.toISOString(), end: dates.end.toISOString() };
    if (siteFilter) params.site = siteFilter;
    return params;
  }, [siteFilter, dateRange]);

  // Fetch zones from API
  const { data: zones, loading, error, refetch } = useZones(apiFilters);

  // Get sites list for filter display
  const sitesList = useRecoilValue(sitesListAtom);

  // Get site name for filter display
  const filterSiteName = useMemo(() => {
    if (!siteFilter || !sitesList?.length) return null;
    const site = sitesList.find(s => String(s.site_id) === siteFilter);
    return site?.site_name;
  }, [sitesList, siteFilter]);

  const clearSiteFilter = () => {
    setSearchParams({});
  };
  const { createZone, updateZone, deleteZone, loading: mutationLoading } = useZoneMutations();

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    if (!zones) return { statuses: [] };
    const statuses = [...new Set(zones.map(z => z.status).filter(Boolean))];
    return { statuses };
  }, [zones]);

  // Helper to format zone data from API
  const formatZone = (z) => ({
    id: z.zone_id || z.id,
    name: z.zone_name || z.name,
    site: z.site_name || z.site || 'Unknown Site',
    site_id: z.site_id,
    size: z.size || `${z.width || 300}x${z.height || 250}`,
    campaigns: z.campaign_count || z.campaigns || 0,
    impressions: z.total_impressions || z.impressions || 0,
    clicks: z.clicks || 0,
    ctr: z.ctr || 0,
    status: z.status || 'active',
    zoneType: z.zone_type || 'web',
  });

  // Apply local filters
  const filteredZones = useMemo(() => {
    if (!zones) return [];
    let result = zones.map(formatZone);

    // Local dropdown filters (only apply site if no URL filter)
    if (!siteFilter && filters.site) {
      result = result.filter(z => String(z.site_id) === filters.site);
    }
    if (filters.status) {
      result = result.filter(z => z.status === filters.status);
    }
    if (filters.zoneType) {
      result = result.filter(z => z.zoneType === filters.zoneType);
    }

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones, siteFilter, filters]);

  const zonesList = filteredZones;

  // Count active local filters
  const activeFilterCount = Object.values(filters).filter(v => v).length;

  // Clear a single local filter
  const clearLocalFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  // Clear all local filters
  const clearAllFilters = () => {
    setFilters({ site: '', status: '', zoneType: '' });
  };

  const openCodeModal = (zone) => {
    setSelectedZone(zone);
    onCodeOpen();
  };

  const handleCampaignsClick = (zone) => {
    navigate(`/campaigns?zone=${zone.id}`);
  };

  const handleSiteClick = (zone) => {
    // Navigate to zones filtered by this site to show all zones for the site
    navigate(`/zones?site=${zone.site_id}`);
  };

  const handleZoneClick = (zone) => {
    // Open edit modal to show zone details
    handleEdit(zone);
  };

  const handleEdit = (zone) => {
    // Find the original zone data
    const originalZone = zones.find(z => (z.zone_id || z.id) === zone.id);
    setSelectedZone(originalZone);
    onEditOpen();
  };

  const handleDeleteClick = (zone) => {
    setZoneToDelete(zone);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!zoneToDelete) return;

    try {
      await deleteZone(zoneToDelete.id);
      toast({
        title: 'Zone deleted',
        description: `"${zoneToDelete.name}" has been deleted.`,
        status: 'success',
        duration: 3000,
      });
      onDeleteClose();
      setZoneToDelete(null);
      refetch();
    } catch (err) {
      toast({
        title: 'Error deleting zone',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleCreateSubmit = async (data) => {
    try {
      await createZone(data);
      toast({
        title: 'Zone created',
        description: `"${data.name}" has been created.`,
        status: 'success',
        duration: 3000,
      });
      onCreateClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error creating zone',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleEditSubmit = async (data) => {
    if (!selectedZone) return;

    try {
      await updateZone(selectedZone.zone_id || selectedZone.id, data);
      toast({
        title: 'Zone updated',
        description: `"${data.name}" has been updated.`,
        status: 'success',
        duration: 3000,
      });
      onEditClose();
      setSelectedZone(null);
      refetch();
    } catch (err) {
      toast({
        title: 'Error updating zone',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            bgGradient="linear(to-r, brand.500, accent.500)"
            bgClip="text"
            fontWeight="700"
          >
            Ad Zones
          </Heading>
          <Text color={textSecondary} mt={1}>
            Manage your website ad placements
          </Text>
        </Box>
        <HStack spacing={3} flexWrap="wrap">
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showCompare={false}
            size="md"
          />
          <Popover
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            placement="bottom-end"
          >
            <PopoverTrigger>
              <Button
                leftIcon={<FontAwesomeIcon icon={faFilter} />}
                variant="outline"
                size={{ base: 'sm', md: 'md' }}
                borderRadius="xl"
                onClick={onFilterOpen}
              >
                Filter
                {activeFilterCount > 0 && (
                  <Badge
                    colorScheme="brand"
                    borderRadius="full"
                    ml={2}
                    fontSize="xs"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              bg={cardBg}
              borderColor={borderColor}
              borderRadius="xl"
              w="280px"
              boxShadow="lg"
            >
              <PopoverHeader fontWeight="600" borderColor={borderColor}>
                Filter Zones
              </PopoverHeader>
              <PopoverCloseButton />
              <PopoverBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm">Site</FormLabel>
                    <Select
                      placeholder="All sites"
                      value={filters.site}
                      onChange={(e) => setFilters(prev => ({ ...prev, site: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {sitesList.map(s => (
                        <option key={s.site_id} value={s.site_id}>{s.site_name}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Status</FormLabel>
                    <Select
                      placeholder="All statuses"
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {filterOptions.statuses.map(s => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Zone Type</FormLabel>
                    <Select
                      placeholder="All types"
                      value={filters.zoneType}
                      onChange={(e) => setFilters(prev => ({ ...prev, zoneType: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      <option value="web">Website</option>
                      <option value="email">Email</option>
                    </Select>
                  </FormControl>
                </VStack>
              </PopoverBody>
              {activeFilterCount > 0 && (
                <PopoverFooter borderColor={borderColor}>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={clearAllFilters}
                    leftIcon={<FontAwesomeIcon icon={faTimes} />}
                  >
                    Clear all filters
                  </Button>
                </PopoverFooter>
              )}
            </PopoverContent>
          </Popover>
          <Button
            leftIcon={<FontAwesomeIcon icon={faPlus} />}
            colorScheme="brand"
            size={{ base: 'sm', md: 'md' }}
            borderRadius="xl"
            onClick={() => protectAction(onCreateOpen)}
            isDisabled={isReadOnly}
            title={isReadOnly ? 'License required to create zones' : undefined}
          >
            New Zone
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {(() => {
        const totals = zonesList.reduce(
          (acc, z) => ({
            campaigns: acc.campaigns + (z.campaigns || 0),
            impressions: acc.impressions + (z.impressions || 0),
            clicks: acc.clicks + (z.clicks || 0),
          }),
          { campaigns: 0, impressions: 0, clicks: 0 }
        );
        return (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, teal.400, teal.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Zones</Text>
                  <Text fontSize="2xl" fontWeight="bold">{zonesList.length}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, blue.400, blue.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Campaigns</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatFullNumber(totals.campaigns)}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, purple.400, purple.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Impressions</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatFullNumber(totals.impressions)}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, green.400, green.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Clicks</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatFullNumber(totals.clicks)}</Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        );
      })()}

      {/* Active Filter Display */}
      {(siteFilter || activeFilterCount > 0) && (
        <Flex mb={4} gap={2} flexWrap="wrap">
          {/* URL-based site filter */}
          {siteFilter && (
            <Tag size="lg" borderRadius="full" colorScheme="blue" variant="subtle">
              <TagLabel>Site: {filterSiteName || `#${siteFilter}`}</TagLabel>
              <TagCloseButton onClick={clearSiteFilter} />
            </Tag>
          )}
          {/* Local dropdown filters */}
          {!siteFilter && filters.site && (
            <Tag size="md" borderRadius="full" colorScheme="blue" variant="subtle">
              <TagLabel>Site: {sitesList.find(s => String(s.site_id) === filters.site)?.site_name}</TagLabel>
              <TagCloseButton onClick={() => clearLocalFilter('site')} />
            </Tag>
          )}
          {filters.status && (
            <Tag size="md" borderRadius="full" colorScheme="green" variant="subtle">
              <TagLabel>Status: {filters.status}</TagLabel>
              <TagCloseButton onClick={() => clearLocalFilter('status')} />
            </Tag>
          )}
          {filters.zoneType && (
            <Tag size="md" borderRadius="full" colorScheme={filters.zoneType === 'email' ? 'purple' : 'blue'} variant="subtle">
              <TagLabel>Type: {filters.zoneType === 'email' ? 'Email' : 'Website'}</TagLabel>
              <TagCloseButton onClick={() => clearLocalFilter('zoneType')} />
            </Tag>
          )}
        </Flex>
      )}

      {/* Error State */}
      {error && (
        <Alert status="error" borderRadius="xl" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <>
          {/* Mobile Loading */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3 }}
            spacing={4}
            display={{ base: 'grid', lg: 'none' }}
          >
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Skeleton height="20px" mb={2} />
                  <Skeleton height="14px" width="60%" mb={4} />
                  <SkeletonText noOfLines={3} spacing={2} />
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>

          {/* Desktop Loading */}
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} display={{ base: 'none', lg: 'block' }}>
            <CardBody>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height="50px" mb={3} borderRadius="md" />
              ))}
            </CardBody>
          </Card>
        </>
      ) : zonesList.length > 0 ? (
        <>
          {/* Mobile Card View */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3 }}
            spacing={4}
            display={{ base: 'grid', lg: 'none' }}
          >
            {zonesList.map((zone) => (
              <ZoneCard
                key={zone.id}
                zone={zone}
                onOpenCode={openCodeModal}
                onEdit={(z) => protectAction(() => handleEdit(z))}
                onDelete={(z) => protectAction(() => handleDeleteClick(z))}
                onCampaignsClick={handleCampaignsClick}
                onSiteClick={handleSiteClick}
                onZoneClick={handleZoneClick}
                isReadOnly={isReadOnly}
              />
            ))}
          </SimpleGrid>

          {/* Desktop Table View */}
          <Card
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            display={{ base: 'none', lg: 'block' }}
          >
            <CardBody p={0}>
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Zone Name</Th>
                      <Th>Site</Th>
                      <Th>Size</Th>
                      <Th isNumeric>Campaigns</Th>
                      <Th isNumeric>Impressions</Th>
                      <Th isNumeric>Clicks</Th>
                      <Th isNumeric>CTR</Th>
                      <Th>Status</Th>
                      <Th width="100px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {zonesList.map((zone) => (
                      <Tr key={zone.id} _hover={{ bg: hoverBg }} transition="all 0.2s">
                        <Td>
                          <HStack spacing={2}>
                            <Box
                              w="8px"
                              h="8px"
                              borderRadius="full"
                              bg={zone.status === 'active' ? 'green.400' : 'yellow.400'}
                            />
                            <Text
                              fontWeight="600"
                              color="brand.500"
                              cursor="pointer"
                              _hover={{ textDecoration: 'underline' }}
                              onClick={() => handleZoneClick(zone)}
                            >
                              {zone.name}
                            </Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Text
                            color="purple.500"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => handleSiteClick(zone)}
                          >
                            {zone.site}
                          </Text>
                        </Td>
                        <Td>
                          <Badge variant="outline" borderRadius="full">{zone.size}</Badge>
                        </Td>
                        <Td isNumeric>
                          <Text
                            fontWeight="500"
                            color={zone.campaigns > 0 ? 'purple.500' : undefined}
                            cursor={zone.campaigns > 0 ? 'pointer' : 'default'}
                            _hover={zone.campaigns > 0 ? { textDecoration: 'underline' } : {}}
                            onClick={() => zone.campaigns > 0 && handleCampaignsClick(zone)}
                          >
                            {zone.campaigns}
                          </Text>
                        </Td>
                        <Td isNumeric fontWeight="500">{formatFullNumber(zone.impressions)}</Td>
                        <Td isNumeric fontWeight="500">{formatFullNumber(zone.clicks)}</Td>
                        <Td isNumeric fontWeight="500">{zone.ctr}%</Td>
                        <Td>
                          <Badge colorScheme={zone.status === 'active' ? 'green' : 'yellow'} borderRadius="full">
                            {zone.status}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <IconButton
                              icon={<FontAwesomeIcon icon={faCode} />}
                              variant="ghost"
                              size="sm"
                              borderRadius="lg"
                              aria-label="Get code"
                              onClick={() => openCodeModal(zone)}
                            />
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<FontAwesomeIcon icon={faEllipsisV} />}
                                variant="ghost"
                                size="sm"
                                borderRadius="lg"
                              />
                              <Portal>
                                <MenuList zIndex={9999} py={2} borderRadius="xl">
                                  <MenuItem
                                    icon={<FontAwesomeIcon icon={faBullhorn} />}
                                    onClick={() => handleCampaignsClick(zone)}
                                    mx={2}
                                    borderRadius="lg"
                                    w="calc(100% - 16px)"
                                  >
                                    View Campaigns
                                  </MenuItem>
                                  <MenuItem
                                    icon={<FontAwesomeIcon icon={faEdit} />}
                                    onClick={() => protectAction(() => handleEdit(zone))}
                                    isDisabled={isReadOnly}
                                    mx={2}
                                    borderRadius="lg"
                                    w="calc(100% - 16px)"
                                  >
                                    Edit
                                  </MenuItem>
                                  <MenuItem
                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                    color={isReadOnly ? 'gray.400' : 'red.500'}
                                    onClick={() => protectAction(() => handleDeleteClick(zone))}
                                    isDisabled={isReadOnly}
                                    mx={2}
                                    borderRadius="lg"
                                    w="calc(100% - 16px)"
                                  >
                                    Delete
                                  </MenuItem>
                                </MenuList>
                              </Portal>
                            </Menu>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        </>
      ) : (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Text color={textSecondary} textAlign="center" py={8}>
              {siteFilter
                ? 'No zones found for this site.'
                : 'No ad zones found. Create your first zone to get started.'}
            </Text>
          </CardBody>
        </Card>
      )}

      {/* Code Modal */}
      <CodeModal isOpen={isCodeOpen} onClose={onCodeClose} zone={selectedZone} />

      {/* Create Zone Modal */}
      <ZoneModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSubmit={handleCreateSubmit}
        isLoading={mutationLoading}
      />

      {/* Edit Zone Modal */}
      <ZoneModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        onSubmit={handleEditSubmit}
        zone={selectedZone}
        isLoading={mutationLoading}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Zone"
        itemName={zoneToDelete?.name}
        isLoading={mutationLoading}
      />
    </Box>
  );
}

function CodeModal({ isOpen, onClose, zone }) {
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const codeBg = useColorModeValue('gray.50', 'gray.900');
  const navigate = useNavigate();

  // Get server URL (backend runs on port 3059)
  const serverUrl = window.location.origin.replace(':3058', ':3059');

  const websiteCode = zone
    ? `<!-- SF Zone: ${zone.name} -->
<script src="${serverUrl}/serve.js?zone=${zone.id}"></script>`
    : '';

  const wordpressCode = zone ? `[statfalcon_zone id="${zone.id}"]` : '';

  const javascriptCode = zone
    ? `<!-- SF Zone: ${zone.name} (Async) -->
<script>
(function() {
  var s = document.createElement('script');
  s.src = '${serverUrl}/serve.js?zone=${zone.id}';
  s.async = true;
  document.head.appendChild(s);
})();
</script>`
    : '';

  const { hasCopied: hasCopiedWeb, onCopy: onCopyWeb } = useClipboard(websiteCode);
  const { hasCopied: hasCopiedWP, onCopy: onCopyWP } = useClipboard(wordpressCode);
  const { hasCopied: hasCopiedJS, onCopy: onCopyJS } = useClipboard(javascriptCode);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <ModalHeader>
          <HStack spacing={2}>
            <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
              <Icon color="white" boxSize={4}>
                <FontAwesomeIcon icon={faCode} />
              </Icon>
            </Box>
            <Text>Embed Code: {zone?.name}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton borderRadius="full" />
        <ModalBody pb={6}>
          <Tabs colorScheme="brand" variant="soft-rounded">
            <TabList gap={2} mb={4} flexWrap="wrap">
              <Tab borderRadius="lg" fontSize="sm">
                <HStack spacing={2}>
                  <FontAwesomeIcon icon={faGlobe} />
                  <Text>Website</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="lg" fontSize="sm">
                <HStack spacing={2}>
                  <FontAwesomeIcon icon={faCode} />
                  <Text>JavaScript</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="lg" fontSize="sm">
                <HStack spacing={2}>
                  <FontAwesomeIcon icon={faEnvelope} />
                  <Text>Email</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="lg" fontSize="sm">
                <HStack spacing={2}>
                  <FontAwesomeIcon icon={faLayerGroup} />
                  <Text>WordPress</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={3}>
                  <Text fontSize="sm" color={textSecondary}>
                    Add this simple code where you want the ad to appear on your website:
                  </Text>
                  <Textarea
                    value={websiteCode}
                    readOnly
                    fontFamily="mono"
                    fontSize="sm"
                    rows={4}
                    bg={codeBg}
                    borderRadius="xl"
                  />
                  <Button
                    leftIcon={<FontAwesomeIcon icon={hasCopiedWeb ? faCheck : faCopy} />}
                    onClick={onCopyWeb}
                    colorScheme={hasCopiedWeb ? 'green' : 'brand'}
                    size="sm"
                    borderRadius="lg"
                  >
                    {hasCopiedWeb ? 'Copied!' : 'Copy Code'}
                  </Button>
                  <Text fontSize="xs" color={textSecondary}>
                    Ads scale down automatically on screens narrower than the zone. To serve a
                    different zone on phones instead, add{' '}
                    <Text as="span" fontFamily="mono">data-mobile-zone="ID"</Text> to the script
                    tag (breakpoint 767px, override with{' '}
                    <Text as="span" fontFamily="mono">data-mobile-max</Text>).
                  </Text>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={3}>
                  <Text fontSize="sm" color={textSecondary}>
                    Use this self-contained JavaScript code for dynamic placement:
                  </Text>
                  <Textarea
                    value={javascriptCode}
                    readOnly
                    fontFamily="mono"
                    fontSize="sm"
                    rows={12}
                    bg={codeBg}
                    borderRadius="xl"
                  />
                  <Button
                    leftIcon={<FontAwesomeIcon icon={hasCopiedJS ? faCheck : faCopy} />}
                    onClick={onCopyJS}
                    colorScheme={hasCopiedJS ? 'green' : 'brand'}
                    size="sm"
                    borderRadius="lg"
                  >
                    {hasCopiedJS ? 'Copied!' : 'Copy Code'}
                  </Button>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={3}>
                  <Text fontSize="sm" color={textSecondary}>
                    Email snippets are generated per ad so the ad ID and
                    campaign ID get baked into the tracking URLs.
                  </Text>
                  <Box fontSize="sm" p={3} bg={codeBg} borderRadius="lg">
                    <Text mb={2}>To get an email snippet:</Text>
                    <Text as="ol" pl={5} fontSize="sm" color={textSecondary}>
                      <li>Open the <strong>Ads</strong> page</li>
                      <li>Click the <strong>⋮</strong> menu on the ad you want to embed</li>
                      <li>Choose <strong>Email Snippet</strong> and pick this zone</li>
                    </Text>
                  </Box>
                  <Text fontSize="xs" color={textSecondary}>
                    Per-issue breakdown: create a new campaign per newsletter
                    issue, attach the ad, then generate a fresh snippet.
                  </Text>
                  <Button
                    leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
                    onClick={() => {
                      onClose();
                      navigate('/ads');
                    }}
                    colorScheme="brand"
                    size="sm"
                    borderRadius="lg"
                    alignSelf="flex-start"
                  >
                    Go to Ads
                  </Button>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={3}>
                  <Text fontSize="sm" color={textSecondary}>
                    Use this shortcode in WordPress posts or pages:
                  </Text>
                  <Code
                    p={4}
                    borderRadius="xl"
                    display="block"
                    fontSize="sm"
                    bg={codeBg}
                  >
                    {wordpressCode}
                  </Code>
                  <Button
                    leftIcon={<FontAwesomeIcon icon={hasCopiedWP ? faCheck : faCopy} />}
                    onClick={onCopyWP}
                    colorScheme={hasCopiedWP ? 'green' : 'brand'}
                    size="sm"
                    borderRadius="lg"
                  >
                    {hasCopiedWP ? 'Copied!' : 'Copy Code'}
                  </Button>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default ZonesPage;
